import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Agent } from "@/types/agent/agent";

export type ListenerRecordingStatus = "idle" | "recording" | "uploading" | "processing" | "done" | "error";

interface UseListenerRecordingOptions {
  agent: Agent | null;
  language?: string;
  /** Optional hint for the max number of distinct speakers, improves diarization accuracy */
  numSpeakers?: number;
}

interface UseListenerRecordingReturn {
  status: ListenerRecordingStatus;
  elapsedSeconds: number;
  conversationId: number | null;
  errorMessage: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  reset: () => void;
}

const getMimeType = (): string => {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
};

const getExtension = (mimeType: string): string => {
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("ogg")) return "ogg";
  return "mp4";
};

export const useListenerRecording = ({ agent, language = "en", numSpeakers }: UseListenerRecordingOptions): UseListenerRecordingReturn => {
  const { userProfile } = useAuth();
  const [status, setStatus] = useState<ListenerRecordingStatus>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (status === "recording") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [status]);

  useEffect(() => () => cleanup(), [cleanup]);

  const processingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const subscribeToCompletion = useCallback((convId: number) => {
    const channel = supabase
      .channel("conversations")
      .on("broadcast", { event: "completed" }, (payload) => {
        if (payload.payload?.conversation_id === convId) {
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
            processingTimeoutRef.current = null;
          }
          setStatus("done");
          supabase.removeChannel(channel);
          realtimeChannelRef.current = null;
        }
      })
      .subscribe();
    realtimeChannelRef.current = channel;

    // If no webhook arrives within 3 minutes, nudge the user to check their Inbox
    processingTimeoutRef.current = setTimeout(() => {
      setStatus("done");
      processingTimeoutRef.current = null;
    }, 3 * 60 * 1000);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setErrorMessage(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(1000);
      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      setStatus("recording");

      timerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (err) {
      setErrorMessage("Microphone access was denied. Please allow microphone permissions and try again.");
      setStatus("error");
    }
  }, []);

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const durationSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);

    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    const mimeType = recorder.mimeType || getMimeType();
    const ext = getExtension(mimeType);
    const blob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];

    setStatus("uploading");

    try {
      const userId = userProfile?.id;
      if (!userId || !agent?.id) throw new Error("User or agent not available");

      const fileName = `${crypto.randomUUID()}.${ext}`;
      const recordingPath = `meeting-recordings/${userId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("agent-documents")
        .upload(recordingPath, blob, { contentType: mimeType });

      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

      // Insert conversation row
      const { data: conversation, error: insertError } = await supabase
        .from("conversations")
        .insert({
          agent_id: agent.id,
          user_id: userId,
          type: "recording",
          metadata: {
            status: "processing",
            duration_seconds: durationSeconds,
            recording_path: recordingPath,
            language,
          },
        })
        .select("id")
        .single();

      if (insertError || !conversation) throw new Error(`Failed to create conversation: ${insertError?.message}`);

      const convId = conversation.id;
      setConversationId(convId);

      setStatus("processing");

      const SYNC_THRESHOLD_SECONDS = 120;

      if (durationSeconds <= SYNC_THRESHOLD_SECONDS) {
        // Short recording — transcribe + analyse synchronously in handle-conversation
        // Gets a signed URL server-side via the elevenlabs function, then calls process-scribe directly
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from("agent-documents")
          .createSignedUrl(recordingPath, 3600);

        if (signedUrlError || !signedUrlData?.signedUrl) throw new Error("Failed to generate signed URL");

        const { error: scribeError } = await supabase.functions.invoke("handle-conversation", {
          body: {
            action: "process-scribe",
            data: { conversationId: convId, audioUrl: signedUrlData.signedUrl, language, numSpeakers },
          },
        });

        if (scribeError) {
          cleanup();
          setErrorMessage("Something went wrong. Please try again.");
          setStatus("error");
        } else {
          setStatus("done");
        }
      } else {
        // Long recording — async webhook flow
        subscribeToCompletion(convId);

        const { error: scribeError } = await supabase.functions.invoke("elevenlabs", {
          body: {
            resource: "scribe",
            action: "transcribe",
            params: { conversationId: convId, recordingPath, language, numSpeakers },
          },
        });

        if (scribeError) {
          cleanup();
          setErrorMessage("Something went wrong. Please try again.");
          setStatus("error");
        }
      }
    } catch (err) {
      setErrorMessage("Something went wrong. Please try again.");
      setStatus("error");
    }
  }, [userProfile, agent, language, numSpeakers, subscribeToCompletion]);

  const reset = useCallback(() => {
    cleanup();
    setStatus("idle");
    setElapsedSeconds(0);
    setConversationId(null);
    setErrorMessage(null);
  }, [cleanup]);

  return { status, elapsedSeconds, conversationId, errorMessage, startRecording, stopRecording, reset };
};
